<template>
  <div class="gesp-code-list">
    <div class="gesp-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">GESP 编程题</h1>
      </div>
      <div class="header-right">
        <el-tag v-if="loading" size="mini" type="info" icon="el-icon-loading">加载中...</el-tag>
        <el-tag v-else size="mini" type="success">共 {{ rows.length }} 题</el-tag>
      </div>
    </div>

    <div class="gesp-body">
      <div class="left-panel">
        <div class="panel-title">题目总览</div>

        <div class="type-menu">
          <div class="type-item active">
            <span class="type-name">编程题</span>
            <span class="type-count">{{ completedCount }}/{{ rows.length }}</span>
          </div>
        </div>

        <div class="number-panel">
          <div class="number-title">编程题</div>
          <div class="number-grid">
            <div
              v-for="(row, idx) in rows"
              :key="String(row.id || idx)"
              class="num"
              :class="{ active: idx + 1 === activeNo, completed: row.hasSubmitted }"
              @click="openDetail(row, idx + 1)"
            >
              {{ idx + 1 }}
            </div>
          </div>
        </div>
      </div>

      <div class="right-panel">
        <div class="table-card">
          <el-table :data="rows" border style="width: 100%" v-loading="loading">
            <el-table-column prop="number" label="标号" width="160" />
            <el-table-column prop="title" label="标题" />
            <el-table-column prop="yearDisplay" label="年份" width="100" />
            <el-table-column label="状态" width="120">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.latestSubmitStatus === 'AC' ? 'success' : (scope.row.hasSubmitted ? 'warning' : 'info')">
                  {{ scope.row.latestSubmitStatus === 'AC' ? '已通过' : (scope.row.hasSubmitted ? '已提交' : '未提交') }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120">
              <template slot-scope="scope">
                <el-button type="text" @click="openDetail(scope.row, scope.$index + 1)">进入</el-button>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="!loading && !rows.length" class="empty-tip">当前科目暂无编程题</div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { pagingGespQuestions } from '@/api/gespQuestion'
import { getLatestSubmitBatch } from '@/api/gespCodeSubmit'
import { getTokenInfo } from '@/utils/jwtUtils'

export default {
  name: 'GESPCodeList',
  computed: {
    completedCount() {
      return (this.rows || []).filter(row => String(row.latestSubmitStatus || '').toUpperCase() === 'AC').length
    }
  },
  data() {
    return {
      loading: false,
      activeNo: 1,
      rows: []
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.loadQuestions()
  },
  beforeDestroy() {
    this.showSidebarAndNavbar()
  },
  methods: {
    hideSidebarAndNavbar() {
      this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) sidebar.style.display = 'none'
      if (navbar) navbar.style.display = 'none'

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = '0'
        appMain.style.margin = '0'
      }
    },
    showSidebarAndNavbar() {
      this.$store.dispatch('app/openSideBar', { withoutAnimation: false })

      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')

      if (sidebar) sidebar.style.display = ''
      if (navbar) navbar.style.display = ''

      const appMain = document.querySelector('.app-main')
      if (appMain) {
        appMain.style.padding = ''
        appMain.style.margin = ''
      }
    },
    extractLevelValue(raw) {
      const m = String(raw || '').trim().toUpperCase().match(/(?:GESP|CCF).*?(?:LEVEL)?\s*([1-8])/)
      return m && m[1] ? Number(m[1]) : null
    },
    parseYear(question) {
      const year = question?.year || question?.examYear || question?.exam_year || ''
      if (year) return String(year)
      const number = String(question?.number || '')
      const m = number.match(/(20\d{2})/)
      return m && m[1] ? m[1] : '--'
    },
    async loadQuestions() {
      this.loading = true
      try {
        const q = this.$route.query || {}
        const level = this.extractLevelValue(q.level || q.courseTypeCode)
        const codeRepoId = Number(q.codeRepoId || 27) || 27
        const resp = await pagingGespQuestions({
          pageNum: 1,
          pageSize: 500,
          repoId: codeRepoId,
          level: level || undefined
        })
        const raw = resp?.data?.data ?? resp?.data ?? {}
        const records = Array.isArray(raw?.records) ? raw.records : (Array.isArray(raw?.list) ? raw.list : (Array.isArray(raw) ? raw : []))
        this.rows = (records || []).map(item => ({
          ...item,
          yearDisplay: this.parseYear(item),
          hasSubmitted: false,
          latestSubmitStatus: ''
        }))
        await this.loadLatestSubmitStatus()
      } catch (e) {
        console.error('加载 GESP 编程题列表失败:', e)
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async loadLatestSubmitStatus() {
      try {
        const user = getTokenInfo && getTokenInfo()
        const userId = user && user.id
        if (!userId) return
        const ids = (this.rows || []).map(item => item && item.id).filter(Boolean)
        if (!ids.length) return
        const resp = await getLatestSubmitBatch(userId, ids)
        const map = resp?.data?.data ?? resp?.data ?? {}
        this.rows = (this.rows || []).map(item => {
          const latest = map ? (map[String(item.id)] || map[item.id]) : null
          const status = latest && latest.status ? String(latest.status).toUpperCase() : ''
          return {
            ...item,
            hasSubmitted: !!latest,
            latestSubmitStatus: status
          }
        })
      } catch (e) {
        console.error('加载编程题提交状态失败:', e)
      }
    },
    openDetail(row, no) {
      if (!row || !row.id) return
      this.activeNo = no
      const q = this.$route.query || {}
      this.$router.push({
        path: '/gesp-code-detail',
        query: {
          questionId: row.id,
          level: q.level || undefined,
          courseTypeCode: q.courseTypeCode || undefined,
          repoId: q.repoId || undefined,
          codeRepoId: q.codeRepoId || 27,
          source: 'gesp-code-list'
        }
      })
    },
    goBack() {
      const q = this.$route.query || {}
      this.$router.push({
        path: '/amc-course',
        query: {
          ...q,
          level: q.level || undefined,
          courseTypeCode: q.courseTypeCode || 'GESP',
          repoId: q.repoId || undefined
        }
      })
    }
  }
}
</script>

<style scoped>
.gesp-code-list { min-height: 100vh; background: #f5f7fa; }
.gesp-header { background: #fff; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 2px 8px rgba(0,0,0,0.06); position: sticky; top: 0; z-index: 10; }
.back-btn { font-size: 18px; color: #666; cursor: pointer; padding: 4px; border-radius: 50%; }
.back-btn:hover { background: #f5f5f5; }
.page-title { margin: 0; font-size: 16px; font-weight: 600; }

.gesp-body { display: flex; gap: 12px; padding: 12px; }
.left-panel { width: 240px; background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; }
.right-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 12px; }
.panel-title { font-weight: 600; color: #333; margin-bottom: 10px; }

.type-menu { display: flex; flex-direction: column; gap: 6px; }
.type-item { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-radius: 8px; cursor: pointer; color: #333; background: #ecf5ff; border: 1px solid #409EFF; }
.type-name { font-weight: 600; }
.type-count { color: #666; font-size: 12px; }

.number-panel { margin-top: 12px; }
.number-title { font-weight: 600; color: #333; margin-bottom: 8px; }
.number-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
.num { height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 6px; cursor: pointer; border: 1px solid #e5e7eb; color: #333; background: #fff; font-size: 13px; }
.num.active { border-color: #409EFF; background: #ecf5ff; }
.num.completed { border-color: #67c23a; color: #67c23a; }

.table-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; }
.tip-card { background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 12px; color: #666; line-height: 1.7; }
.empty-tip { text-align: center; color: #909399; padding: 32px 0 12px; }
</style>
